import { useEffect, useState } from "react";
import { Truck, RefreshCw, FileDown, BadgeEuro } from "lucide-react";
import { Input } from "../ui/input";
import { toast } from "sonner";
import api, { API, eur } from "../../lib/api";

const STATUS = {
  AWAITING_TRANSFER: ["Attesa bonifico", "bg-amber-100 text-amber-800"],
  PAID: ["Pagato", "bg-blue-100 text-blue-800"],
  SHIPPED: ["Spedito", "bg-green-100 text-green-800"],
  PENDING: ["In attesa pagamento", "bg-amber-100 text-amber-800"],
  PROCESSING: ["In elaborazione", "bg-amber-100 text-amber-800"],
  FAILED: ["Non pagato", "bg-red-100 text-red-800"],
  CANCELLED: ["Annullato", "bg-stone-100 text-stone-600"],
};

export default function MobileShopOrders({ initialFilter }) {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState(initialFilter || "PAID");
  const [openId, setOpenId] = useState(null);
  const [shipId, setShipId] = useState(null);
  const [tracking, setTracking] = useState("");

  const load = () => api.get("/shop/orders").then(({ data }) => setOrders(data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const ship = async (id) => {
    try { await api.post(`/shop/orders/${id}/ship`, { tracking }); toast.success("Ordine spedito, cliente avvisato"); setShipId(null); setTracking(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Operazione non riuscita"); }
  };
  const markPaid = async (id) => {
    try { await api.post(`/shop/orders/${id}/mark-paid`); toast.success("Pagamento confermato, ricevuta inviata"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Operazione non riuscita"); }
  };


  const list = orders.filter((o) => filter === "all" || o.status === filter);
  const toShip = orders.filter((o) => o.status === "PAID").length;

  return (
    <div data-testid="mobile-shop-orders">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-serif text-xl font-semibold text-[#0B2A45]">Negozio online {toShip > 0 && <span className="text-sm bg-blue-100 text-blue-800 rounded-full px-2 py-0.5 align-middle" data-testid="mobile-shop-toship">{toShip} da spedire</span>}</h2>
        <button onClick={load} className="p-2 text-stone-500" data-testid="mobile-shop-refresh"><RefreshCw className="w-5 h-5" /></button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 mb-3 -mx-4 px-4">
        {[["AWAITING_TRANSFER", "Bonifici"], ["PAID", "Da spedire"], ["SHIPPED", "Spediti"], ["all", "Tutti"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`mobile-shop-filter-${v}`}
            className={`shrink-0 px-4 h-9 rounded-full text-sm font-semibold ${filter === v ? "bg-[#0F4C81] text-white" : "bg-white border border-stone-200 text-stone-600"}`}>{l}</button>
        ))}
      </div>

      <div className="space-y-3">
        {list.length === 0 && <p className="text-center text-stone-400 py-10">Nessun ordine</p>}
        {list.map((o) => {
          const [label, cls] = STATUS[o.status] || [o.status, "bg-stone-100 text-stone-700"];
          const c = o.customer || {};
          return (
            <div key={o.order_id} className="bg-white rounded-2xl border border-stone-200 p-4" data-testid={`mobile-shop-order-${o.order_id}`}>
              <button onClick={() => setOpenId(openId === o.order_id ? null : o.order_id)} className="w-full text-left" data-testid={`mobile-shop-toggle-${o.order_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0B2A45] truncate">{c.name || "Cliente"}</div>
                    <div className="text-xs text-stone-500 truncate">{c.city}{c.city && " · "}{new Date(o.created_at).toLocaleDateString("it-IT")} · {o.order_id}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-semibold text-[#0F4C81]">{eur(o.total)}</div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>{label}</span>
                  </div>
                </div>
              </button>
              {openId === o.order_id && (
                <div className="mt-3 pt-3 border-t border-stone-100 text-sm space-y-1" data-testid={`mobile-shop-items-${o.order_id}`}>
                  {(o.items || []).map((it, i) => (
                    <div key={`${it.product_id || it.name}-${i}`} className="flex justify-between text-stone-600"><span>{it.quantity} × {it.name}</span><span className="font-mono">{eur((it.unit_price || 0) * (it.quantity || 0))}</span></div>
                  ))}
                  <div className="text-xs text-stone-500 pt-2">{c.address}, {c.zip} {c.city} {c.province}</div>
                  <div className="text-xs text-stone-500">{c.email} {c.phone && `· ${c.phone}`}</div>
                  {o.tracking && <div className="text-xs text-stone-600 pt-1">Tracking: <b>{o.tracking}</b></div>}
                  <a href={`${API}/shop/orders/${o.order_id}/invoice.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#0F4C81] pt-1" data-testid={`mobile-shop-invoice-${o.order_id}`}><FileDown className="w-3.5 h-3.5" />Ricevuta PDF</a>
                </div>
              )}
              {o.status === "AWAITING_TRANSFER" && (
                <div className="mt-3 pt-3 border-t border-stone-100">
                  <button onClick={() => markPaid(o.order_id)} className="w-full h-11 rounded-xl bg-[#15803D] text-white font-semibold flex items-center justify-center gap-1" data-testid={`mobile-shop-markpaid-${o.order_id}`}><BadgeEuro className="w-4 h-4" />Segna come pagato (bonifico ricevuto)</button>
                </div>
              )}
              {o.status === "PAID" && (
                <div className="mt-3 pt-3 border-t border-stone-100">
                  {shipId === o.order_id ? (
                    <div className="space-y-2">
                      <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Codice tracking (opzionale)" className="h-11" data-testid={`mobile-shop-tracking-${o.order_id}`} />
                      <div className="flex gap-2">
                        <button onClick={() => ship(o.order_id)} className="flex-1 h-11 rounded-xl bg-[#15803D] text-white font-semibold flex items-center justify-center gap-1" data-testid={`mobile-shop-ship-confirm-${o.order_id}`}><Truck className="w-4 h-4" />Conferma spedizione</button>
                        <button onClick={() => { setShipId(null); setTracking(""); }} className="h-11 px-4 rounded-xl bg-stone-50 text-stone-600" data-testid={`mobile-shop-ship-cancel-${o.order_id}`}>Annulla</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => { setShipId(o.order_id); setTracking(""); }} className="w-full h-11 rounded-xl bg-[#0F4C81] text-white font-semibold flex items-center justify-center gap-1" data-testid={`mobile-shop-ship-${o.order_id}`}><Truck className="w-4 h-4" />Spedito</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
